import React, { useState, useContext, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Alert,
  ListGroup,
  ListGroupItem,
  Form,
  Stack,
  Dropdown,
  Button,
} from "react-bootstrap";
import { LuEthernetPort } from "react-icons/lu";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";
import { useFetchData } from "../helpers/FetchDataHelpers";

export default function ScaleGatewayDetails(props) {
  const actions = React.useMemo(
    () => ({
      CONNECT: "CONNECT",
      REMOVE: "REMOVE",
      NO_ACTION: "NO_ACTION",
    }),
    [],
  );

  const { gateway, deploymentId, gondolaId, refetchGondolaInfo } = props;

  const [ongoingAction, setOngoingAction] = useState(actions.NO_ACTION);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [nautobotJobId, setNautobotJobId] = useState(null);
  const [nautobotJobTimeElapsed, setNautobotJobTimeElapsed] = useState(0);

  const fetchData = useFetchData();
  const { userenv } = useContext(UserContext);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!nautobotJobId) {
        return;
      }
      const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
      const url = `${baseUrl}/gondolas/${gondolaId}/gateways/${gateway.scale_gateway_id}/ip-address?nautobot_job_id=${nautobotJobId}`;
      const { isResponseOK, data, errorMessage } = await fetchData(
        url,
        "PATCH",
        false,
      );
      if (isResponseOK && data) {
        if (data.job_status === "SUCCESS") {
          setNautobotJobId(null);
          setNautobotJobTimeElapsed(0);
          toast.success("Network configuration job completed!");
          refetchGondolaInfo();
          setOngoingAction(actions.NO_ACTION);
        }
        setNautobotJobTimeElapsed(nautobotJobTimeElapsed + 5);
      } else {
        toast.error("Error: " + errorMessage);
        setNautobotJobId(null);
        setNautobotJobTimeElapsed(0);
        setOngoingAction(actions.NO_ACTION);
      }
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [
    nautobotJobId,
    fetchData,
    userenv,
    deploymentId,
    actions,
    gateway,
    gondolaId,
    refetchGondolaInfo,
    nautobotJobTimeElapsed,
  ]);

  const onConnectGateway = () => {
    setButtonsDisabled(true);
    const connectGateway = async () => {
      const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
      const url = `${baseUrl}/gondolas/${gondolaId}/gateways/${gateway.scale_gateway_id}/connect`;
      const { isResponseOK, data, errorMessage } = await fetchData(
        url,
        "PATCH",
        false,
      );
      if (!isResponseOK) {
        toast.error("Error: " + errorMessage);
      } else {
        setNautobotJobId(data.nautobot_job_id);
        setOngoingAction(actions.NO_ACTION);
      }
      setButtonsDisabled(false);
    };
    connectGateway();
  };

  const onRemoveGateway = () => {
    setButtonsDisabled(true);
    const removeGateway = async () => {
      const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
      const url = `${baseUrl}/gondolas/${gondolaId}/gateways/${gateway.scale_gateway_id}?force_delete=false`;
      const { isResponseOK, errorMessage } = await fetchData(url, "DELETE");
      if (!isResponseOK) {
        toast.error("Error: " + errorMessage);
      } else {
        toast.success("Gateway removed successfully");
        setOngoingAction(actions.NO_ACTION);
        refetchGondolaInfo();
      }
      setButtonsDisabled(false);
    };
    removeGateway();
  };

  return (
    <Stack gap={3} className="mt-4">
      <Stack
        direction="horizontal"
        gap={3}
        className="justify-content-between align-items-center"
      >
        <h5 className="mb-0">Gateway {gateway.backend_id}</h5>
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            size="sm"
            disabled={buttonsDisabled}
          >
            Actions
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                setOngoingAction(actions.CONNECT);
              }}
            >
              <LuEthernetPort className="me-2" />
              Connect
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                setOngoingAction(actions.REMOVE);
              }}
            >
              <FaTrash className="me-2" />
              Remove
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </Stack>

      {ongoingAction === actions.CONNECT && (
        <Stack gap={3} direction="horizontal" className="mt-3">
          <span>Connect gateway ?</span>
          <Button
            disabled={buttonsDisabled}
            className="flex-grow-1"
            size="sm"
            variant="danger"
            onClick={() => {
              setOngoingAction(actions.NO_ACTION);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={buttonsDisabled}
            className="flex-grow-1"
            variant="success"
            size="sm"
            onClick={() => {
              onConnectGateway();
              setOngoingAction(actions.NO_ACTION);
            }}
          >
            Confirm
          </Button>
        </Stack>
      )}

      {ongoingAction === actions.REMOVE && (
        <Stack gap={3} direction="horizontal">
          <span>Remove Gateway ?</span>
          <Button
            disabled={buttonsDisabled}
            className="flex-grow-1"
            variant="danger"
            size="sm"
            onClick={() => {
              setOngoingAction(actions.NO_ACTION);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={buttonsDisabled}
            className="flex-grow-1"
            variant="success"
            size="sm"
            onClick={() => {
              onRemoveGateway();
              setOngoingAction(actions.NO_ACTION);
            }}
          >
            Confirm
          </Button>
        </Stack>
      )}

      {nautobotJobId && (
        <>
          <Alert className="mt-3" variant="warning">
            Waiting for network configuration job ({nautobotJobId}) to
            complete...
            <br />
            Time elapsed: {nautobotJobTimeElapsed} seconds
          </Alert>
          <Alert variant="danger">
            Warning: Do not plug the network cable and don&apos;t close this window
            until the job is completed!
          </Alert>
        </>
      )}

      <ListGroup>
        <ListGroupItem>Backend Id: &nbsp; {gateway.backend_id}</ListGroupItem>
        <ListGroupItem>MAC Address: &nbsp; {gateway.mac_address}</ListGroupItem>
        <ListGroupItem>IP Address: &nbsp; {gateway.ip_address}</ListGroupItem>
        <ListGroupItem>
          Connected: &nbsp; &nbsp;
          <Form.Check
            inline
            disabled
            checked={gateway.connection_check}
            type="checkbox"
          />
        </ListGroupItem>
        <ListGroupItem>
          QA State: &nbsp; &nbsp;
          <Form.Check
            inline
            disabled
            checked={gateway.qa_check}
            type="checkbox"
          />
        </ListGroupItem>
        <ListGroupItem>QA Comment: &nbsp; {gateway.qa_comment}</ListGroupItem>
        <ListGroupItem>
          Gondolas Count: &nbsp; {gateway.associated_gondolas.length}
        </ListGroupItem>
      </ListGroup>
    </Stack>
  );
}
